// api.ts's private verb helpers throw `new Error(JSON.stringify(errorEnvelope))`,
// so `err.message` is the whole `{ error: { reason, message, ... } }` document as a
// string. Every caller that wants to show a human-readable failure has to parse it
// back out; this is that parse, in one place.
//
// The field order matters: `message` is the server's own sentence when it wrote
// one, `reason` is the machine code, and the raw string is the last resort for a
// failure that never reached the API at all (a network drop, a CORS preflight).

export interface ApiErrorEnvelope {
  reason?: string
  message?: string
  detail?: string
  [key: string]: unknown
}

/** The error envelope, or null when the failure was not an API response. */
export function parseApiError(err: unknown): ApiErrorEnvelope | null {
  const raw = err instanceof Error ? err.message : String(err)
  try {
    const parsed = JSON.parse(raw) as { error?: ApiErrorEnvelope }
    return parsed.error ?? null
  } catch {
    return null
  }
}

/** A sentence to show the operator. Never returns an empty string. */
export function readApiError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err)
  const envelope = parseApiError(err)
  return envelope?.message ?? envelope?.detail ?? envelope?.reason ?? raw
}

/** The machine-readable reason code, for branching on a specific failure. */
export function apiErrorReason(err: unknown): string | null {
  return parseApiError(err)?.reason ?? null
}
